import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Flame,
  Languages,
  Newspaper,
  NotebookPen,
  Scale,
  Sparkles,
} from "lucide-react";

const tickerItems = [
  "RBI holds repo rate at 6.5% — markets rally",
  "ISRO reusable launch vehicle clears key test",
  "Monsoon arrives 4 days early, IMD confirms",
  "Parliament passes Urban Transport Bill",
  "India U-19 team lifts Asia Cup",
  "Green hydrogen mission crosses 1GW milestone",
  "Supreme Court verdict on digital privacy today",
  "Rupee strengthens against dollar for third week",
];

const PhoneMockup = () => (
  <div className="relative mx-auto w-[290px] sm:w-[320px]">
    <div className="rounded-[2.6rem] border border-slate-700/80 bg-slate-950 p-2.5 shadow-[0_0_90px_rgba(6,182,212,0.18)]">
      <div className="rounded-[2.1rem] bg-[#0D1420] overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-2 text-[10px] font-mono-x text-slate-500">
          <span>9:41</span>
          <span className="flex items-center gap-1.5 tracking-widest">
            <img src="/logo-icon.png" alt="" className="h-4 w-4" />
            INNEWZ
          </span>
          <span className="flex gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            LIVE
          </span>
        </div>
        <div className="px-4 pb-5 space-y-3">
          <p className="mono-label !text-[9px]">Morning Brief • 60 words</p>
          <div className="rounded-xl border border-[#1E293B] bg-[#111827] p-4">
            <span className="text-[9px] font-mono-x uppercase tracking-widest text-amber-400">Economy</span>
            <h4 className="font-display font-semibold text-sm mt-1 leading-snug">
              RBI holds rates, markets cheer
            </h4>
            <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
              The central bank kept the repo rate at 6.5% for the eighth straight policy. Sensex jumped
              412 points as bank stocks led gains. Analysts expect the first cut next quarter.
            </p>
            <div className="flex items-center justify-between mt-3 text-[9px] font-mono-x text-slate-500">
              <span>47 WORDS • 18 SEC</span>
              <span className="text-cyan-400">14 SOURCES</span>
            </div>
          </div>
          <div className="rounded-xl border border-[#1E293B] bg-[#111827] p-3">
            <div className="flex items-center justify-between text-[9px] font-mono-x uppercase tracking-widest">
              <span className="text-slate-400">Truth Score</span>
              <span className="text-emerald-400">92 / 100</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800 mt-2 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "92%" }}
                transition={{ duration: 1.4, delay: 0.6, ease: "easeOut" }}
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400"
              />
            </div>
            <div className="flex items-center gap-1.5 mt-2.5 text-[10px] text-slate-400">
              <BadgeCheck className="h-3.5 w-3.5 text-cyan-400" />
              AI verified • Zero bias flags
            </div>
          </div>
          <div className="flex justify-around pt-1 text-slate-500">
            <Newspaper className="h-4 w-4 text-cyan-400" />
            <Scale className="h-4 w-4" />
            <Languages className="h-4 w-4" />
            <NotebookPen className="h-4 w-4" />
          </div>
        </div>
      </div>
    </div>

    <motion.div
      animate={{ y: [0, -10, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -left-10 top-24 hidden sm:flex items-center gap-2 rounded-full border border-[#1E293B] bg-[#111827]/90 backdrop-blur px-3.5 py-2 text-xs"
    >
      <BadgeCheck className="h-4 w-4 text-emerald-400" />
      Fact-checked
    </motion.div>
    <motion.div
      animate={{ y: [0, 10, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -right-12 top-48 hidden sm:flex items-center gap-2 rounded-full border border-[#1E293B] bg-[#111827]/90 backdrop-blur px-3.5 py-2 text-xs"
    >
      <Sparkles className="h-4 w-4 text-cyan-400" />
      AI Verified
    </motion.div>
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      className="absolute -right-6 bottom-16 hidden sm:flex items-center gap-2 rounded-full border border-[#1E293B] bg-[#111827]/90 backdrop-blur px-3.5 py-2 text-xs"
    >
      <Flame className="h-4 w-4 text-amber-400" />
      60-sec reads
    </motion.div>
  </div>
);

const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative grid-lines">
    <div className="pointer-events-none absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full bg-cyan-500/10 blur-[120px]" />
    <div className="pointer-events-none absolute top-40 right-0 h-[380px] w-[380px] rounded-full bg-blue-600/10 blur-[120px]" />

    <div className="pt-16">
      <div data-testid="news-ticker" className="border-y border-[#1E293B] bg-[#0B111C]/80 overflow-hidden">
        <div className="flex w-max whitespace-nowrap animate-ticker py-2.5">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="mx-6 flex items-center gap-2 text-[11px] font-mono-x uppercase tracking-widest text-slate-400">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-7">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span data-testid="hero-badge" className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-1.5 text-xs font-mono-x uppercase tracking-widest text-cyan-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            News, verified — not just delivered
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-display font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-6"
        >
          Read the news in
          <span className="block mt-2 bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-500 bg-clip-text text-transparent">
            60 words.
          </span>
          <span className="block mt-2 text-slate-400">Know it's true.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
        >
          InNewZ compresses every story into a sharp 60-word brief, cross-checks it across
          multiple sources, scores it with AI verification, and speaks your language — literally.
          Built for readers, citizens, and exam aspirants.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href="#waitlist"
            data-testid="hero-cta-waitlist"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-7 py-3.5 text-sm font-semibold text-white transition-[filter,transform] duration-200 hover:brightness-110 active:scale-95"
          >
            Join the Waitlist
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href="#features"
            data-testid="hero-cta-features"
            className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-7 py-3.5 text-sm font-semibold text-slate-300 transition-colors duration-200 hover:border-cyan-500/50 hover:text-cyan-300"
          >
            Explore Features
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs font-mono-x uppercase tracking-widest text-slate-500"
        >
          <span>• 7+ regional languages</span>
          <span>• Zero-noise feed</span>
          <span>• Citizen reporting</span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.25 }}
        className="lg:col-span-5"
      >
        <PhoneMockup />
      </motion.div>
    </div>
  </section>
);

export default Hero;
